import axios from 'axios';
import { normalizeBusinessLine } from '@/utils/petmed-metrics.utils';

const adminUrl = process.env.VUE_APP_ADMIN_URL;

// Los montos DECIMAL de Postgres pueden llegar como string; el contrato los
// promete number, esto es solo defensivo.
const toNumber = (value) => (value == null ? value : Number(value));

function normalizeInsights(data) {
  if (!data) return data;
  const totals = data.totals || {};
  return {
    ...data,
    totals: {
      ...totals,
      invoicedTotal: toNumber(totals.invoicedTotal),
      pawerCoverage: toNumber(totals.pawerCoverage),
      paidToClinics: toNumber(totals.paidToClinics),
      pendingToClinics: toNumber(totals.pendingToClinics),
      userPayment: toNumber(totals.userPayment),
      avgTicket: toNumber(totals.avgTicket),
      coverageRate: toNumber(totals.coverageRate),
      historicalAttendances: toNumber(totals.historicalAttendances),
    },
    byBusinessLine: normalizeBusinessLine(data.byBusinessLine),
    byClinic: (data.byClinic || []).map((c) => ({
      ...c,
      byBusinessLine: normalizeBusinessLine(c.byBusinessLine),
      pawerCoverage: toNumber(c.pawerCoverage),
      userPayment: toNumber(c.userPayment),
      paid: { count: c.paid?.count ?? 0, amount: toNumber(c.paid?.amount ?? 0) },
      pending: { count: c.pending?.count ?? 0, amount: toNumber(c.pending?.amount ?? 0) },
    })),
    byPawerService: (data.byPawerService || []).map((s) => ({ ...s, pawerCoverage: toNumber(s.pawerCoverage) })),
    byClinicService: (data.byClinicService || []).map((s) => ({ ...s, pawerCoverage: toNumber(s.pawerCoverage) })),
  };
}

function normalizeTrend(data) {
  return (data || []).map((t) => ({
    ...t,
    pawerCoverage: toNumber(t.pawerCoverage),
    userPayment: toNumber(t.userPayment),
  }));
}

function buildParams(filters) {
  const params = {};
  Object.entries(filters).forEach(([key, value]) => {
    if (value == null) return;
    if (Array.isArray(value) && value.length === 0) return;
    params[key] = value;
  });
  return params;
}

export default class PetmedMetricsService {
  static getHeaders() {
    const token = JSON.parse(localStorage.getItem('accessToken'));
    return { Authorization: `Bearer ${token}` };
  }

  static async getInsights({ fromDate, toDate, clinicIds } = {}) {
    try {
      const res = await axios.get(`${adminUrl}/petmed-attendances/insights`, {
        headers: this.getHeaders(),
        params: buildParams({ fromDate, toDate, clinicIds }),
      });
      return { status: 'ok', data: normalizeInsights(res.data.data) };
    } catch (err) {
      console.error('[PetmedMetrics] getInsights error:', err?.response?.data || err.message);
      return { status: 'error', data: null, message: err?.response?.data?.message };
    }
  }

  // Detalle de "Atenciones y monto por clínica": atenciones paginadas de una clínica.
  static async getAttendances({ fromDate, toDate, clinicId, page, pageSize } = {}) {
    try {
      const res = await axios.get(`${adminUrl}/petmed-attendances/attendances`, {
        headers: this.getHeaders(),
        params: buildParams({ fromDate, toDate, clinicIds: [clinicId], page, pageSize }),
      });
      const items = (res.data.data || []).map((a) => ({
        ...a,
        pawerCoverage: toNumber(a.pawerCoverage),
        userPayment: toNumber(a.userPayment),
      }));
      return { status: 'ok', data: items, meta: res.data.meta };
    } catch (err) {
      console.error('[PetmedMetrics] getAttendances error:', err?.response?.data || err.message);
      return { status: 'error', data: null, message: err?.response?.data?.message };
    }
  }

  static async getTrend({ granularity, fromDate, toDate, clinicIds } = {}) {
    try {
      const res = await axios.get(`${adminUrl}/petmed-attendances/trend`, {
        headers: this.getHeaders(),
        params: buildParams({ granularity, fromDate, toDate, clinicIds }),
      });
      return { status: 'ok', data: normalizeTrend(res.data.data) };
    } catch (err) {
      console.error('[PetmedMetrics] getTrend error:', err?.response?.data || err.message);
      return { status: 'error', data: null, message: err?.response?.data?.message };
    }
  }
}
